import { useEffect } from "react";
import { useLocation } from "react-router";

/**
 * Scrolls to the element matching `window.location.hash` whenever the
 * route changes. Required for cross-page hash links (e.g. clicking
 * `/#case-studies` from `/products/digital-workers`) — react-router
 * does not handle in-page anchor scroll on its own.
 *
 * Routes through the global Lenis instance (window.__lenis) when present
 * because native scrollIntoView is silently overridden by Lenis. Calls
 * `lenis.resize()` first since the destination route may have just
 * mounted and changed document height — without it, scrollTo can clamp
 * to a stale max scroll value.
 *
 * Element resolution is retried for up to ~1.5s (30 × 50ms) to cover
 * Suspense / lazy section mounts.
 */
export function ScrollToHash() {
    const { pathname, hash } = useLocation();

    // Scroll to top on route change when there's no hash target
    useEffect(() => {
        if (hash) return;
        const lenis = window.__lenis;
        // Reset native scroll position directly
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        // Sync Lenis internal state so it doesn't lerp back
        if (lenis) {
            lenis.scrollTo(0, { immediate: true });
        }
    }, [pathname, hash]);

    useEffect(() => {
        if (!hash) return;
        const id = hash.startsWith("#") ? hash.slice(1) : hash;

        let cancelled = false;
        let attempts = 0;

        const tryScroll = () => {
            if (cancelled) return;
            const el = document.getElementById(id);
            const lenis = window.__lenis;

            if (el && lenis) {
                lenis.resize();
                lenis.scrollTo(el, { offset: 0, lock: true });
                return;
            }
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }

            attempts += 1;
            if (attempts < 30) {
                window.setTimeout(tryScroll, 50);
            }
        };

        // Defer one frame so the destination route has rendered.
        const raf = window.requestAnimationFrame(tryScroll);
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(raf);
        };
    }, [pathname, hash]);

    return null;
}
